import React, { useEffect, useState } from 'react';
import { formatDate } from '../utils/dateUtils';
import useShipDate from '../features/setting/shipDate/useShipDate';

const DualShipDateDropdown = ({
  handleDataChange,
  manage = 'auto',
  /** When true (e.g. /admin/ordersummary), show multi ship-date checkboxes. Public customer flow keeps a single select. */
  allowMultiShipDate = false,
}) => {
  const { shipDates, getShipDates } = useShipDate({ manage });
  const [airShipDates, setAirShipDates] = useState([]);
  const [seaShipDates, setSeaShipDates] = useState([]);
  const [selectedType, setSelectedType] = useState('all'); // 'all', 'air', 'sea'
  const [selectedShipDate, setSelectedShipDate] = useState('all');
  /** 'all' | 'none' | 'multi' — only when allowMultiShipDate and selectedType is air or sea */
  const [dateMode, setDateMode] = useState('all');
  const [selectedDateIds, setSelectedDateIds] = useState(() => new Set());

  useEffect(() => {
    getShipDates();
  }, []);

  useEffect(() => {
    if (shipDates && shipDates.length > 0) {
      const airDates = shipDates.filter(date => date.isAir === true);
      const seaDates = shipDates.filter(date => date.isAir === false);
      setAirShipDates(airDates);
      setSeaShipDates(seaDates);
    }
  }, [shipDates]);

  const emitMulti = (nextIds, type) => {
    const ids = Array.from(nextIds).map((id) =>
      typeof id === 'string' ? parseInt(id, 10) : id
    );
    handleDataChange({
      shipDate: 'multi',
      shipDateIds: ids,
      multiShipType: type === 'air' ? 'air' : 'sea',
    });
  };

  const handleTypeChange = (e) => {
    const type = e.target.value;
    setSelectedType(type);
    setDateMode('all');
    setSelectedDateIds(new Set());
    setSelectedShipDate('all');

    if (type === 'all') {
      handleDataChange('all');
    } else if (type === 'air') {
      handleDataChange('allAir');
    } else if (type === 'sea') {
      handleDataChange('allShip');
    }
  };

  const handleShipDateChange = (e) => {
    const value = e.target.value;
    setSelectedShipDate(value);

    if (value === 'all') {
      if (selectedType === 'air') {
        handleDataChange('allAir');
      } else if (selectedType === 'sea') {
        handleDataChange('allShip');
      }
    } else if (value === 'none') {
      if (selectedType === 'air') {
        handleDataChange('noneAir');
      } else if (selectedType === 'sea') {
        handleDataChange('noneShip');
      }
    } else {
      handleDataChange(value);
    }
  };

  const handleDateModeChange = (e) => {
    const mode = e.target.value;
    setDateMode(mode);
    setSelectedDateIds(new Set());

    const st = selectedType;
    if (mode === 'all') {
      if (st === 'air') handleDataChange('allAir');
      else if (st === 'sea') handleDataChange('allShip');
    } else if (mode === 'none') {
      if (st === 'air') handleDataChange('noneAir');
      else if (st === 'sea') handleDataChange('noneShip');
    }
    // mode === 'multi': parent is updated when user checks boxes (or stays on empty multi until then)
  };

  const toggleDateId = (id, checked) => {
    const key = String(id);
    const next = new Set(selectedDateIds);
    if (checked) next.add(key);
    else next.delete(key);
    setSelectedDateIds(next);
    const shipKind = selectedType === 'air' ? 'air' : 'sea';
    if (next.size === 0) {
      handleDataChange({
        shipDate: 'multi',
        shipDateIds: [],
        multiShipType: shipKind,
      });
    } else {
      emitMulti(next, shipKind);
    }
  };

  const selectAllInCategory = () => {
    const options = getCurrentOptions();
    const allKeys = new Set(options.map((d) => String(d.id)));
    setSelectedDateIds(allKeys);
    const shipKind = selectedType === 'air' ? 'air' : 'sea';
    emitMulti(allKeys, shipKind);
  };

  const clearDateSelection = () => {
    setSelectedDateIds(new Set());
    const shipKind = selectedType === 'air' ? 'air' : 'sea';
    handleDataChange({
      shipDate: 'multi',
      shipDateIds: [],
      multiShipType: shipKind,
    });
  };

  const getCurrentOptions = () => {
    switch (selectedType) {
      case 'air':
        return airShipDates;
      case 'sea':
        return seaShipDates;
      default:
        return [];
    }
  };

  const getTypeLabel = () => {
    switch (selectedType) {
      case 'air':
        return 'รอบเครื่องบิน';
      case 'sea':
        return 'รอบเรือ';
      default:
        return 'รอบเรือ/เครื่องบิน';
    }
  };

  return (
    <div className="flex flex-col space-y-4">
      <div className="flex flex-col space-y-2">
        <label className="text-lg text-gray-700">รอบการจัดส่ง</label>
        <div className="flex space-x-4 flex-wrap gap-y-2">
          <label className="flex items-center space-x-2">
            <input
              type="radio"
              name="shipType"
              value="all"
              checked={selectedType === 'all'}
              onChange={handleTypeChange}
              className="text-blue-600"
            />
            <span>ทั้งหมด</span>
          </label>
          <label className="flex items-center space-x-2">
            <input
              type="radio"
              name="shipType"
              value="air"
              checked={selectedType === 'air'}
              onChange={handleTypeChange}
              className="text-blue-600"
            />
            <span>รอบเครื่องบิน</span>
          </label>
          <label className="flex items-center space-x-2">
            <input
              type="radio"
              name="shipType"
              value="sea"
              checked={selectedType === 'sea'}
              onChange={handleTypeChange}
              className="text-blue-600"
            />
            <span>รอบเรือ</span>
          </label>
        </div>
      </div>

      {(selectedType === 'air' || selectedType === 'sea') && !allowMultiShipDate && (
        <div className="flex flex-col space-y-2">
          <label htmlFor="shipDateSelect" className="text-lg text-gray-700">
            เลือก{getTypeLabel()}
          </label>
          <select
            id="shipDateSelect"
            name="shipDate"
            value={selectedShipDate}
            onChange={handleShipDateChange}
            className="px-4 py-1.5 border border-gray-400 rounded-md"
          >
            <option value="all">ทั้งหมด</option>
            <option value="none">ไม่มี</option>
            {getCurrentOptions().map((date) => (
              <option key={date.uuid} value={date.id}>
                {`${formatDate(date.shipDate)}${date.isAir && date.warehouseReceivingAddress ? ` [${date.warehouseReceivingAddress.addressNickName}]` : ''}`}
              </option>
            ))}
          </select>
        </div>
      )}

      {(selectedType === 'air' || selectedType === 'sea') && allowMultiShipDate && (
        <div className="flex flex-col space-y-3">
          <span className="text-lg text-gray-700">เลือก{getTypeLabel()}</span>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <label className="flex items-center space-x-2">
              <input
                type="radio"
                name="dateMode"
                value="all"
                checked={dateMode === 'all'}
                onChange={handleDateModeChange}
                className="text-blue-600"
              />
              <span>ทั้งหมด</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="radio"
                name="dateMode"
                value="none"
                checked={dateMode === 'none'}
                onChange={handleDateModeChange}
                className="text-blue-600"
              />
              <span>ไม่มี</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="radio"
                name="dateMode"
                value="multi"
                checked={dateMode === 'multi'}
                onChange={() => {
                  setDateMode('multi');
                  setSelectedDateIds(new Set());
                  const shipKind = selectedType === 'air' ? 'air' : 'sea';
                  handleDataChange({
                    shipDate: 'multi',
                    shipDateIds: [],
                    multiShipType: shipKind,
                  });
                }}
                className="text-blue-600"
              />
              <span>เลือกหลายรอบ</span>
            </label>
          </div>

          {dateMode === 'multi' && (
            <div className="border border-gray-300 rounded-md p-3 bg-gray-50 max-w-xl">
              <div className="flex flex-wrap gap-2 mb-3">
                <button
                  type="button"
                  className="text-sm px-2 py-1 bg-white border border-gray-400 rounded hover:bg-gray-100"
                  onClick={selectAllInCategory}
                >
                  เลือกทั้งหมดในรายการ
                </button>
                <button
                  type="button"
                  className="text-sm px-2 py-1 bg-white border border-gray-400 rounded hover:bg-gray-100"
                  onClick={clearDateSelection}
                >
                  ล้าง
                </button>
              </div>
              <div className="max-h-48 overflow-y-auto space-y-2 pr-1">
                {getCurrentOptions().map((date) => (
                  <label
                    key={date.uuid}
                    className="flex items-start space-x-2 cursor-pointer text-sm"
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5 text-blue-600 shrink-0"
                      checked={selectedDateIds.has(String(date.id))}
                      onChange={(e) => toggleDateId(date.id, e.target.checked)}
                    />
                    <span>
                      {`${formatDate(date.shipDate)}${
                        date.isAir && date.warehouseReceivingAddress
                          ? ` [${date.warehouseReceivingAddress.addressNickName}]`
                          : ''
                      }`}
                    </span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-600 mt-2">
                เลือกได้หลายรอบ — ต้องเลือกอย่างน้อย 1 รอบก่อนกดค้นหา
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DualShipDateDropdown;
